import type { TExample } from '@atlas/uicorn/preview/modules';

import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, useTransition } from 'react';

import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';
import useRefCallback from 'use-ref-callback';
import Button from '@atlas/uicorn/lib/Control/Button';
import IconButton from '@atlas/uicorn/lib/Control/IconButton';
import Emoji from '@atlas/uicorn/lib/Data/Emoji';
import Dialog, { type TDialogRef } from '@atlas/uicorn/lib/Display/Dialog';
import Text from '@atlas/uicorn/lib/Display/Text';
import CrossIcon from '@atlas/uicorn/lib/Icon/CrossIcon';
import Spinner from '@atlas/uicorn/lib/Display/Spinner';
import FormItem from '@atlas/uicorn/lib/Control/FormItem';
import Form from '@atlas/uicorn/lib/Control/Form';
import Input from '@atlas/uicorn/lib/Input/Input';
import Select from '@atlas/uicorn/lib/Input/Select';

const Modal = React.memo(
    React.forwardRef(function Modal(props: TModalProps, ref: React.ForwardedRef<TDialogRef>): React.ReactElement {
        const {
            onClose,
            children,
            header,
            footer,
            noScroll,
            noBodyPadding,
            noBackdropClick,
            onCloseEvent,
            onBackdropClick,
            onClick,
            onKeyDown,
            ...dialogProps
        } = props;

        const [, startTransition] = useTransition();
        const [isClosing, setIsClosing] = useState(false);
        const [dialogEl, setDialogEl] = useState<HTMLDialogElement | null>(null);
        const closeTimeoutRef = useRef<NodeJS.Timeout>();
        const openedViaKeyboardRef = useRef(false);

        // Track if modal was opened via keyboard by checking :focus-visible on the trigger element
        useLayoutEffect(
            function trackOpenMethod() {
                if (!dialogEl) return;
                const activeEl = document.activeElement;
                openedViaKeyboardRef.current = activeEl?.matches(':focus-visible') ?? false;
            },
            [dialogEl],
        );

        const handleCloseAnimation = useRefCallback(
            (e: React.SyntheticEvent<HTMLDialogElement | HTMLButtonElement, Event>) => {
                if (isClosing) return; // Prevent multiple close attempts

                onCloseEvent?.(e);

                if (e.defaultPrevented) return;

                if (!openedViaKeyboardRef.current) {
                    (document.activeElement as HTMLElement)?.blur?.();
                }

                // Start transition for closing state
                startTransition(() => {
                    setIsClosing(true);
                });

                // Ensure modal stays mounted for animation duration
                closeTimeoutRef.current = setTimeout(() => {
                    onClose?.(e);
                }, unmountTime * 1000);
            },
        );

        useImperativeHandle<TModalRef | null, TModalRef | null>(ref, () => dialogEl ?? null, [dialogEl]);

        // Cleanup timeout on unmount
        useEffect(() => {
            return () => {
                if (closeTimeoutRef.current) {
                    clearTimeout(closeTimeoutRef.current);
                }
            };
        }, []);

        // Apply closing class and debug animation
        useLayoutEffect(() => {
            if (dialogEl && isClosing) {
                dialogEl.classList.add('closing');
            }
        }, [dialogEl, isClosing]);

        useLayoutEffect(
            function openDialog() {
                if (!dialogEl || dialogEl.open) return;
                dialogEl.showModal();

                requestAnimationFrame(() => {
                    const autofocusEl = dialogEl.querySelector<HTMLElement>('[data-autofocus="true"]');
                    if (autofocusEl) {
                        autofocusEl.focus();
                        return;
                    }

                    const firstInput = dialogEl.querySelector<HTMLElement>(
                        [
                            'input:not([type="hidden"]):not([tabindex="-1"]):not([readonly]):not(:disabled):not([data-disabled="true"])',
                            'textarea:not([readonly]):not(:disabled):not([data-disabled="true"])',
                            '[role="listbox"]:not(:disabled):not([data-disabled="true"])',
                            '[contenteditable="true"]:not([data-disabled="true"])',
                            '.ant-checkbox-input:not(:disabled)',
                            '.ant-switch:not(.ant-switch-disabled)',
                        ].join(', '),
                    );
                    if (firstInput) {
                        firstInput.focus();
                        return;
                    }

                    dialogEl.focus();
                });
            },
            [dialogEl],
        );

        const handleClickOutsideDialog = useRefCallback(function handleClickOutsideDialog(
            event: React.MouseEvent<HTMLDialogElement, MouseEvent>,
        ) {
            if (event.target === event.currentTarget) {
                const dialogDimensions = event.currentTarget.getBoundingClientRect();
                if (
                    event.clientX < dialogDimensions.left ||
                    event.clientX > dialogDimensions.right ||
                    event.clientY < dialogDimensions.top ||
                    event.clientY > dialogDimensions.bottom
                ) {
                    if (noBackdropClick) return;
                    onBackdropClick?.(event);
                    if (!event.defaultPrevented) {
                        handleCloseAnimation(event);
                    }
                }
            }

            onClick?.(event);
        });

        const handleKeyDown = useRefCallback<React.KeyboardEventHandler<HTMLDialogElement>>((event) => {
            onKeyDown?.(event);
            // TODO: It doesn't prevent all CommandBar callbacks but it does so for table navigation
            event.stopPropagation();
        });

        return (
            <SModalDialog
                ref={setDialogEl}
                tabIndex={-1}
                onMouseDown={handleClickOutsideDialog}
                onClose={handleCloseAnimation}
                onKeyDown={handleKeyDown}
                className={isClosing ? 'closing' : ''}
                {...dialogProps}
            >
                {(onClose || onCloseEvent) && (
                    <SCloseButton icon={<CrossIcon />} tabIndex={-1} tooltip="" onClick={handleCloseAnimation} />
                )}
                {header && (
                    <SModalHeaderContainer>
                        {typeof header === 'string' ? <Text h="3">{header}</Text> : header}
                    </SModalHeaderContainer>
                )}
                <SModalBody children={children} $noScroll={!!noScroll} $noBodyPadding={!!noBodyPadding} />
                {footer && <SModalFooter children={footer} />}
            </SModalDialog>
        );
    }),
);

export default Modal;

// #region Helpers

export const unmountTime = 0.2;

// Temporary workaround for the issue with missing focus on 2nd level modal
export const onUnmountCallback = (cb: () => void) => {
    setTimeout(() => {
        cb();
    }, unmountTime * 1e3);
};

// #endregion

// #region Styles

const animateBackdropAppearance = keyframes`
    to {
        // color variables are not available, because it is rendered in #top-layer next to :root
        background-color: rgba(38, 38, 38, 0.64);
    }
`;

const animateModalAppearance = keyframes`
    from {
        opacity: 0;
        translate: 0% -20%;
        scale: 0.8;
    }

    to {
        opacity: 1;
        translate: 0% 0%;
        scale: 1;
    }
`;

const unmountKeyframes = keyframes`
    from {
        opacity: 1;
        translate: 0% 0%;
        scale: 1;
    }
    to {
        opacity: 0;
        translate: 0% -30%;
        scale: 1;
    }
`;

const SModalDialog = styled(Dialog).attrs({ elevation: 'medium', modal: true })`
    // TODO: Carefully swap them (double check all usages of the modal)
    --modal-spacing: 1.6rem;
    --modal-close-button-size: 2.4rem;

    padding: 0;

    width: 30vw;
    ${(p) =>
        p.theme.when.screenVariant(
            'compact',
            css`
                width: calc(100vw - 2.4rem);
            `,
        )}
    min-width: 38.0rem;
    max-width: 57.6rem; // 1920px screen / 3
    max-height: 75vh;
    overflow: visible;
    margin-top: 15vh;
    flex-direction: column;

    border: ${(p) => `0.1rem solid ${p.theme.color.grey62}`};
    border-radius: ${(p) => p.theme.radius.medium};

    animation: ${animateModalAppearance} 0.3s ease forwards;

    &.closing {
        animation-name: ${unmountKeyframes} !important;
        animation-duration: ${unmountTime}s !important;
        animation-timing-function: ease !important;
        animation-fill-mode: forwards !important;

        &::backdrop {
            opacity: 0 !important;
            transition: opacity ${unmountTime}s ease !important;
        }
    }

    &::backdrop {
        animation: ${animateBackdropAppearance} 0.3s ease forwards;
    }
`;

const SModalHeaderContainer = styled.header`
    position: sticky;
    top: 0;
    width: 100%;

    padding: var(--modal-spacing);
    &:not(:last-child) {
        padding-bottom: calc(var(--modal-spacing) / 2);
    }
    // TODO: Replace with SCloseButton
    &:not(:first-child) {
        padding-right: calc(var(--modal-close-button-size) + var(--modal-spacing));
    }

    display: flex;
    justify-content: space-between;

    color: ${(p) => p.theme.color.grey9};
`;

export const SModalBody = styled.section<{ $noScroll: boolean; $noBodyPadding: boolean }>`
    overflow: ${(p) => (p.$noScroll ? 'visible' : 'auto')};

    padding: ${(p) => (p.$noBodyPadding ? '0' : `var(--modal-spacing)`)};
    &:not(:last-child) {
        padding-bottom: calc(var(--modal-spacing) / 2);
    }
    ${SModalHeaderContainer} + & {
        padding-top: calc(var(--modal-spacing) / 2);
    }
`;

const SModalFooter = styled.footer`
    position: sticky;
    bottom: 0;
    width: 100%;

    padding: var(--modal-spacing);
    ${SModalHeaderContainer} + &, ${SModalBody} + & {
        padding-top: calc(var(--modal-spacing) / 2);
    }

    display: flex;
    justify-content: flex-end;
    align-items: center;
    column-gap: 1.6rem;

    font: ${(p) => p.theme.font.input};
`;

export const SCloseButton = styled(IconButton)`
    position: absolute;
    top: var(--modal-spacing);
    right: var(--modal-spacing);
    z-index: 5;
`;

// #endregion

// #region Types

export type TModalRef = TDialogRef;

export type TModalProps = Omit<React.HTMLAttributes<HTMLDialogElement>, 'title'> & {
    header?: React.ReactNode;
    footer?: React.ReactNode;
    noScroll?: boolean; // TODO: It is currently used to fix visibility of the Select dropdowns, can be dropped when Select is using Portal dialog
    noBackdropClick?: boolean;
    noBodyPadding?: boolean;
    onCloseEvent?: (e: React.SyntheticEvent<HTMLDialogElement | HTMLButtonElement, Event>) => void;
    onBackdropClick?: (event: React.MouseEvent<HTMLDialogElement, MouseEvent>) => void;
    onClose?: (e: React.SyntheticEvent<HTMLDialogElement | HTMLButtonElement, Event>) => void;
};

// #endregion

// #region Examples

export const FormExample: TExample = {
    render(props) {
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [isSaving, setIsSaving] = useState(false);
        const [form] = Form.useForm();
        const resetValues = useCallback(() => form.resetFields(), [form]);

        return (
            <>
                <Button onClick={() => setIsModalOpen(true)}>Trigger Modal Form</Button>
                {isModalOpen && (
                    <Modal
                        header="Create notification"
                        onClose={() => {
                            setIsModalOpen(false);
                            resetValues();
                        }}
                        footer={
                            <>
                                {isSaving && <Spinner />}
                                <Button disabled={isSaving} type="tertiary" danger onClick={resetValues}>
                                    Clear values
                                </Button>
                                <Button type="primary" onClick={() => form.submit()} disabled={isSaving}>
                                    Save
                                </Button>
                            </>
                        }
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={(v) => {
                                setIsSaving(true);
                                new Promise((resolve, reject) => {
                                    setTimeout(() => {
                                        try {
                                            resolve(v);
                                        } catch (error) {
                                            reject(error);
                                        }
                                    }, 1e3);
                                }).then((v) => {
                                    props.onFinish(v);
                                    setIsSaving(false);
                                    setIsModalOpen(false);
                                    resetValues();
                                });
                            }}
                        >
                            <Text p color="grey8">
                                Optional description copy. A Dialog is a flexible floating surface, used to display
                                transient content such as menus, selection options, dialogs, and more.
                            </Text>
                            <FormItem label="Notification name" name="name" rules={[{ required: true }]}>
                                <Input placeholder="Enter notification name" />
                            </FormItem>
                            <Text p color="grey8">
                                Optional description copy. A Dialog is a flexible floating surface, used to display
                                transient content such as menus, selection options, dialogs, and more.
                            </Text>
                            <FormItem label="Send via" name="via" rules={[{ required: true }]}>
                                <Select
                                    placeholder="Select channel"
                                    options={[
                                        { key: 'slack', label: 'Slack' },
                                        { key: 'email', label: 'Email' },
                                        { key: 'sms', label: 'SMS' },
                                    ]}
                                />
                            </FormItem>
                        </Form>
                    </Modal>
                )}
            </>
        );
    },
    handlers: {
        onFinish: 'dashboard',
    },
};

export const LayoutExample: TExample = {
    render(props) {
        return (
            <>
                <Modal
                    header={
                        <>
                            <Text h="2">
                                <Emoji emoji="warning" size={16} /> Scrollable modal with a long long Optional
                                description copy
                            </Text>
                        </>
                    }
                    footer={<Button type="primary">Primary action</Button>}
                    onClose={props.onClose}
                >
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                </Modal>
                Refresh to open again
            </>
        );
    },
    handlers: {
        onClose: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('Modal', async () => {
        // const testModule = '@test'; const { render } = await import(/* @vite-ignore */ testModule);

        it.todo('matches snapshot');
    });
}

// #endregion
